import React, { useState, useLayoutEffect, useRef, forwardRef, ForwardedRef, useMemo } from 'react';

import AddNewLineNumberPanel from '../../../../templates/thankyoulotto/Picker/AddNewLineNumberPanel';
import Summary from '../../../../templates/thankyoulotto/Picker/Checkout/Summary';
import { isThankYouLotto } from '../../../../utils/template';
import { countToLastNonEmptyLine } from '../../helpers/linesHelper';
import useLinesHelper from '../../helpers/useLinesHelper';

import NumberPanel from './NumberPanel';

import useLinesManageButtonClick from 'components/play/helpers/useLinesManageButtonClick';
import { numberArray } from 'utils/helpers';

function DesktopPicker({ rowSize }: { rowSize: number }, linesContainerReference: ForwardedRef<HTMLDivElement>) {
    const { lottery, lines, stakes, setLines, EMPTY_LINE } = useLinesHelper();
    const rowCount = useRef<number>(0);
    const [lineCount, setLineCount] = useState<number>(0);

    const isTemplateThankYouLotto = useMemo(() => isThankYouLotto(), []);

    const clampLineCount = function (value: number) {
        return Math.max(rowSize, Math.min(lottery.maxLinesPerTicket, value));
    };

    useLayoutEffect(() => {
        if (isTemplateThankYouLotto) {
            // TODO: check this again when adding new line
            setLineCount(Math.min(lottery.maxLinesPerTicket, rowSize - 1));
            return;
        }

        // hook executes after changing the width of the layout
        const requiredRowCount = Math.ceil(countToLastNonEmptyLine(lines) / rowSize);
        const initialRowCount = Math.ceil(lottery.minLinesPerTicket / rowSize);
        rowCount.current = Math.max(initialRowCount, requiredRowCount);
        // set lineCount to fill all visible rows of number panels
        setLineCount(clampLineCount(rowSize * rowCount.current));
    }, [rowSize]);

    useLinesManageButtonClick((buttonSignal) => {
        const newRowCount = rowCount.current + buttonSignal;
        let newLineCount = clampLineCount(rowSize * newRowCount);

        if (isTemplateThankYouLotto) {
            // Add lines one by one
            newLineCount = Math.min(lottery.maxLinesPerTicket, lineCount + buttonSignal);
        }

        // if changing the number of rows may affect the number of lines, make the change
        if (newLineCount !== lineCount) {
            rowCount.current = newRowCount;
            setLineCount(newLineCount);
        }
    });

    useLayoutEffect(() => {
        if (lineCount) {
            // set empty lines for additional number panels
            setLines(numberArray(lineCount).map((i) => lines?.[i] ?? EMPTY_LINE));
        }
    }, [lineCount]);

    function getContent() {
        if (isTemplateThankYouLotto) {
            return (
                <div className="row align-items-start mt-4 row-gap-4">
                    <div className="col">
                        <div className="row row-gap-4" ref={linesContainerReference}>
                            {lines.map((line, index) => (
                                <div key={index} className="col col-md-4">
                                    <NumberPanel lineNumbers={line} lineIndex={index} stake={stakes[index]} />
                                </div>
                            ))}
                            {lines.length < lottery.maxLinesPerTicket && (
                                <div key="add-new-line" className="col col-md-4">
                                    <AddNewLineNumberPanel />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col col-md-3 col-lg-4">
                        <Summary />
                    </div>
                </div>
            );
        }

        return (
            <div ref={linesContainerReference} className="row w-100">
                {lines.map((line, index) => (
                    <div
                        key={index}
                        className="col-xl-4 col-md-6 col-12 justify-content-center purchase-line-container rounded fade-in"
                    >
                        <NumberPanel lineNumbers={line} lineIndex={index} stake={stakes[index]} />
                    </div>
                ))}
            </div>
        );
    }

    return getContent();
}

export default forwardRef(DesktopPicker);
